
import React, { useState, useEffect } from 'react';
import { CampingSpot, AppUser, CollaborativeMap, SpotCondition, ConditionTag, UserReview, WikiSpot, SvvCamera, Coordinates } from '../types';
import { X, Navigation, ChevronUp, ChevronDown, MapPin, ShieldCheck, RefreshCcw, Loader2, MountainSnow, Lock, Unlock, Home, Flame, Coffee, RotateCcw, Mountain, Share2, Ban, ShieldAlert, Edit3, Binoculars, CheckCircle2, FolderPlus, ArrowLeft, Fish, Zap, Flag, Tent, Recycle, Anchor, Calendar, Info, Star, Sun, Cloud, CloudLightning, CloudSnow, CloudRain, CloudFog, Droplets, Wind, Landmark, FerrisWheel, Heart, LogOut } from 'lucide-react';
import { subscribeToConditions } from '../services/conditionService';
import { logCleanup, formatCleanupAge } from '../services/cleanupService';
import { isSpotOccupied, occupancyDaysRemaining } from '../services/occupancyService';
import { isSpotNearby } from '../services/spotProximity';
import SpotVisitModal from './spots/SpotVisitModal';
import PastVisitModal from './spots/PastVisitModal';
import VehicleVisitsOverlay from './spots/VehicleVisitsOverlay';
import ExtendStayControl from './spots/ExtendStayControl';
import { suggestDeletion, banSpotGlobally, logFeatureUsage, subscribeToSpotImages, getThumbUrl, fetchGooglePhotosForSpot, subscribeToReviews } from '../services/firebaseService';
import { deleteContent } from '../services/myContentService';
import { isSnuplassSpot } from '../services/waypointService';
import { isAutocamperSpot, isUserCreatedSpot, isManagedCampingSpot } from '../utils/autocamperSpots';
import { useLocationImage } from '../hooks/useLocationImage';
import { needsGooglePhotoRefresh } from '../services/spotEnrichment';
import { deleteDoc, doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../services/firebaseService';
import type { SpotImage } from '../services/firebaseService';
import { APP_URL } from '../constants';
import { openExternalNavigation } from '../services/navigationService';
import { ensureSpotShareMetadata } from '../services/shareMetadataService';
import { isAdminUser } from '../services/accessControl';
import WeatherSunBadge from './WeatherSunBadge';
import NatureScoutCard from './NatureScoutCard';
import { useTranslation } from 'react-i18next';
import { useWildlife } from '../hooks/useWildlife';
import { useToast } from '../contexts/ToastContext';
import VehicleBadge from './common/VehicleBadge';
import UserAvatar from './common/UserAvatar';
import SpotProximityFacts from './spots/SpotProximityFacts';
import ContactInfoBlock from './spots/ContactInfoBlock';
import { WebcamBlock, AttractionsBlock } from './spots/SpotMetadataBlocks';
import ReviewSystem from './ReviewSystem';
import { fetchCamerasNearSpot } from '../services/svvCameraService';
import { fetchAttractions } from '../services/wikiService';

function getSatelliteTileInfo(lat: number, lon: number, zoom = 16) {
  const n = Math.pow(2, zoom);
  const xExact = ((lon + 180) / 360) * n;
  const x = Math.floor(xExact);
  const latRad = (lat * Math.PI) / 180;
  const yExact = (1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n;
  const y = Math.floor(yExact);
  
  // Calculate percentage offsets within the current tile
  const offsetX = (xExact - x) * 100;
  const offsetY = (yExact - y) * 100;

  return {
    urls: [
      `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${zoom}/${y}/${x - 1}`,
      `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${zoom}/${y}/${x}`,
      `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${zoom}/${y}/${x + 1}`,
    ],
    offsetX,
    offsetY
  };
}

function getSpotCardSatelliteIcon(spot: CampingSpot, size = 16) {
  const type = spot.spotType || '';
  if (type === 'rasteplass' || type === 'snuplass') return <MapPin size={size} className="text-blue-400" />;
  // Autocamperplads har `spotType: 'algorithmic'`, men skal ha bobilplass-ikonet.
  if (type === 'commercial_campsite' || isAutocamperSpot(spot)) return <Home size={size} className="text-amber-400" />;
  if (type === 'coastal') return <Anchor size={size} className="text-cyan-400" />;
  return <Tent size={size} className="text-emerald-400" />;
}

const WeatherIcon = ({ symbol, size = 16 }: { symbol: string; size?: number }) => {
  if (symbol.includes('clearsky') || symbol.includes('fair'))
    return <Sun size={size} className="text-amber-400" />;
  if (symbol.includes('partlycloudy'))
    return (
      <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
        <Sun size={Math.max(8, size - 4)} className="text-amber-400 absolute -top-1 -right-1" />
        <Cloud size={size} className="text-[var(--text-secondary)]" />
      </div>
    );
  if (symbol.includes('cloudy')) return <Cloud size={size} className="text-[var(--text-secondary)]" />;
  if (symbol.includes('thunder')) return <CloudLightning size={size} className="text-amber-300" />;
  if (symbol.includes('snow') || symbol.includes('sleet'))
    return <CloudSnow size={size} className="text-blue-200" />;
  if (symbol.includes('rain') || symbol.includes('shower'))
    return <CloudRain size={size} className="text-blue-400" />;
  if (symbol.includes('fog')) return <CloudFog size={size} className="text-[var(--text-muted)]" />;
  return <Cloud size={size} className="text-[var(--text-secondary)]" />;
};

interface MobileSpotSheetProps {
  spot: CampingSpot & { distance?: number };
  onClose: () => void;
  onExpand: () => void;
  isFavorite: boolean;
  isVisited?: boolean;
  collaborativeMaps?: CollaborativeMap[];
  onToggleFavorite: (e: React.MouseEvent, id: string) => void;
  onToggleVisit?: (e: React.MouseEvent, id: string) => void;
  onAddPastStay?: (id: string, checkInAt: number, checkedOutAt?: number) => Promise<unknown> | void;
  onDeletePastStay?: (id: string, checkInAt: number) => Promise<unknown> | void;
  onAddToMap?: (e: React.MouseEvent, id: string) => void;
  onReanalyze: (s: CampingSpot) => void;
  isAnalyzing?: boolean;
  user: AppUser | null;
  onRemoveSpot?: (id: string) => void;
  onEdit?: (s: CampingSpot) => void;
  isGeneratingMeta?: boolean;
  showBackToRoute?: boolean;
  onBackToRoute?: () => void;
  showBackToCollabMap?: boolean;
  onBackToCollabMap?: () => void;
  forceShow?: boolean;
  onMarkOccupied?: (spotId: string, days: number, tags: ConditionTag[]) => void;
  onExtendOccupancy?: (spotId: string, days: number) => void;
  onClearOccupancy?: (spotId: string) => void;
  userPosition?: Coordinates | null;
  onOpenAuth?: () => void;
}

const MobileSpotSheet: React.FC<MobileSpotSheetProps> = ({
  spot, onClose, onExpand, isFavorite, isVisited, collaborativeMaps, onToggleFavorite, onToggleVisit, onAddPastStay, onDeletePastStay, onAddToMap, onReanalyze, isAnalyzing, user, onRemoveSpot, onEdit, isGeneratingMeta, showBackToRoute = false, onBackToRoute, showBackToCollabMap = false, onBackToCollabMap, forceShow = false,
  onMarkOccupied, onExtendOccupancy, onClearOccupancy, userPosition, onOpenAuth,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmReport, setConfirmReport] = useState(false);
  const [showNatureCard, setShowNatureCard] = useState(false);
  const [showUtmarkInfo, setShowUtmarkInfo] = useState(false);
  const [showTeslaQr, setShowTeslaQr] = useState(false);
  const checkInCount = typeof spot.checkInCount === 'number' ? spot.checkInCount : 0;
  const checkInNights = typeof spot.checkInNights === 'number' ? spot.checkInNights : 0;
  const [spotImages, setSpotImages] = useState<SpotImage[]>([]);
  const [conditions, setConditions] = useState<SpotCondition[]>([]);
  const [showVisitModal, setShowVisitModal] = useState(false);
  const [showPastVisitModal, setShowPastVisitModal] = useState(false);
  const [showVehicleVisitsOverlay, setShowVehicleVisitsOverlay] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const wildlife = useWildlife(spot?.location?.latitude ?? 0, spot?.location?.longitude ?? 0);
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleExpand = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setIsExpanded(prev => !prev);
    onExpand?.();
  };
  const [activeTab, setActiveTab] = useState<'overview' | 'legal' | 'facilities'>('overview');
  const [spotReviews, setSpotReviews] = useState<UserReview[]>([]);
  const [nearbyCameras, setNearbyCameras] = useState<(SvvCamera & { _distance: number })[]>([]);
  const [failedCameraIds, setFailedCameraIds] = useState<Set<string>>(new Set());
  const [bannerImageFailed, setBannerImageFailed] = useState(false);
  const [showAttractions, setShowAttractions] = useState(false);
  const [attractions, setAttractions] = useState<WikiSpot[]>([]);
  const [isLoadingAttractions, setIsLoadingAttractions] = useState(false);
  const [noAttractionsFound, setNoAttractionsFound] = useState(false);
  const [showCreatorVehicle, setShowCreatorVehicle] = useState(false);

  useEffect(() => {
    setActiveTab('overview');
    setShowAttractions(false);
    setAttractions([]);
    setNoAttractionsFound(false);
    setShowCreatorVehicle(false);
    setNearbyCameras([]);
    setFailedCameraIds(new Set());
  }, [spot?.id]);

  // Hent nærliggende SVV-webkameraer (område-info)
  useEffect(() => {
    if (!spot?.id || !spot.location) return;
    let cancelled = false;
    fetchCamerasNearSpot(spot.location, 500)
      .then(cams => { if (!cancelled) setNearbyCameras(cams); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [spot?.id]);

  const handleToggleAttractions = async () => {
    if (showAttractions) { setShowAttractions(false); return; }
    if (attractions.length > 0) { setShowAttractions(true); return; }
    if (noAttractionsFound) return;
    setIsLoadingAttractions(true);
    try {
      const results = await fetchAttractions(spot.location);
      if (results.length === 0) setNoAttractionsFound(true);
      else { setAttractions(results); setShowAttractions(true); }
    } catch { /* stille */ } finally {
      setIsLoadingAttractions(false);
    }
  };

  useEffect(() => {
    if (!spot?.id) return;
    const unsub = subscribeToReviews(spot.id, (reviews) => {
      setSpotReviews(reviews || []);
    });
    return () => unsub();
  }, [spot?.id]);

  const handleTabClick = (tab: 'overview' | 'legal' | 'facilities') => {
    setActiveTab(tab);
    if (!isExpanded) {
      setIsExpanded(true);
      onExpand?.();
    }
  };

  // Fjernet avstandskrav: brukeren kan oppdatere, sjekke inn og verifisere fra hvor som helst
  const isNearby = true;

  // Står brukeren her selv (sjekket inn og ikke ut)? → tilby forlengelse i stedet
  // for ny innsjekk. Dekker også oppholdet etter at occupiedUntil har utløpt.
  const isStayingHere = (isSpotOccupied(spot) && spot.occupiedBy === user?.uid)
    || (!!user && user.currentCheckIn?.spotId === spot.id);

  const handleCleanup = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user || cleanupLoading) return;
    setCleanupLoading(true);
    try {
      const { newCleanupCount, newBadge } = await logCleanup(spot.id, user.uid);
      if (newBadge) {
        toast.show(`${newBadge.emoji} Nytt merke: ${newBadge.labelNo}! (${newCleanupCount} oppryddinger)`, 'success');
      } else {
        toast.show(`🧹 Opprydning loggført! Du har ryddet ${newCleanupCount} gang${newCleanupCount === 1 ? '' : 'er'}.`, 'success');
      }
    } catch {
      toast.show('Kunne ikke logge opprydning', 'error');
    } finally {
      setCleanupLoading(false);
    }
  };

  // Subscribe to UGC images from Firestore subcollection
  useEffect(() => {
    if (!spot?.id) return;
    const unsub = subscribeToSpotImages(spot.id, setSpotImages);
    return () => unsub();
  }, [spot?.id]);

  useEffect(() => {
    setBannerImageFailed(false);
  }, [spot?.id]);

  // On-demand Google Places bilder — fire-and-forget, kun for relevante spottyper
  useEffect(() => {
    if (!spot?.id) return;
    const SKIP_TYPES = ['forbidden_area', 'snuplass', 'algorithmic'];
    if (SKIP_TYPES.includes(spot.spotType)) return;
    if (needsGooglePhotoRefresh(spot)) {
      fetchGooglePhotosForSpot(spot.id).catch(() => {});
    }
  }, [spot?.id, spot?.googlePhotosCheckedAt, spot?.enrichVersion]);

  useEffect(() => {
    if (!spot?.id) return;
    const unsub = subscribeToConditions(spot.id, setConditions);
    return () => unsub();
  }, [spot?.id]);

  // Delt stedsnøyaktig bilde-fallback (Mapillary / Street View). Kun når plassen
  // mangler både brukerbilder og lagret auto-bilde. Brukerbilder vinner alltid.
  const hasOwnPhotosForLoc = spotImages.length > 0 || !!(spot?.photoUrls && spot.photoUrls.length > 0);
  const locationImage = useLocationImage(
    spot?.location?.latitude,
    spot?.location?.longitude,
    !!spot?.id && !hasOwnPhotosForLoc && !spot?.auto_image_url,
  );

  if (!spot || !spot.location) return null;

  // Collect all available images with source info
  const allImages: Array<{ url: string; source?: SpotImage['source'] }> = [];
  const seen = new Set<string>();
  spotImages.forEach(img => { if (img.image_url && !seen.has(img.image_url)) { seen.add(img.image_url); allImages.push({ url: img.image_url, source: img.source || 'user' }); }});
  spot.photoUrls?.forEach(url => { if (url && !seen.has(url)) { seen.add(url); allImages.push({ url, source: 'user' }); }});
  (spot as any).ugcImages?.forEach((img: any) => { const url = typeof img === 'string' ? img : img?.url; if (url && !seen.has(url)) { seen.add(url); allImages.push({ url, source: 'user' }); }});
  if (allImages.length === 0 && spot.auto_image_url) allImages.push({ url: spot.auto_image_url, source: (spot.auto_image_source as SpotImage['source']) || 'wikimedia' });
  // Delt fallback: stedsnøyaktig Mapillary/Street View når intet annet finnes.
  if (allImages.length === 0 && locationImage?.url) allImages.push({ url: locationImage.url, source: (locationImage.source as SpotImage['source']) });

  const isRasteplass = spot.features.includes("RASTEPLASS") || (spot.spotType === 'rasteplass' && !spot.features.includes("SNUPLASS"));
  const isFirepit = spot.features.includes("BÅLPLASS");
  const isGapahuk = spot.features.includes("GAPAHUK");
  const isPicnic = spot.features.includes("PICNIC");
  const isUtsikt = spot.features.includes("UTSIKT");
  const isSnuplass = isSnuplassSpot(spot);
  const isOsmFacility = spot.spotType === 'osm_amenity';
  // POI-attraksjoner (fornøyelsesparker o.l.) skal ikke ha fricamp-camping-analyse.
  const isAttractionPoi = spot.spotType === 'attraction';
  // Anviste autocamperpladser er bobilplasser: samme ark som de kommersielle
  // (kontaktinfo, bobil-ikon). Se utils/autocamperSpots.ts.
  const isCommercialCampsite = isManagedCampingSpot(spot);
  const isSearchResult = spot.features.includes("SØKERESULTAT");
  const isUtmarkCategoryTag = !isSnuplass && !isRasteplass && !isFirepit && !isPicnic && !isGapahuk && !isUtsikt && !isSearchResult;
  // `sourceLevel: 4` på autocamperpladsene er vern mot opprydningsskriptene, ikke et
  // brukerbidrag — ellers fikk arket rosa «BRUKER»-kilde.
  const normalizedSource = (spot.source || (isUserCreatedSpot(spot) ? 'user' : spot.spotType === 'algorithmic' ? 'ai' : undefined)) as 'nvdb' | 'osm' | 'user' | 'ai' | undefined;
  const sourceBadge = normalizedSource
    ? normalizedSource === 'nvdb'
      ? { label: 'NVDB', className: 'text-blue-300 border-blue-500/40 bg-blue-500/10' }
      : normalizedSource === 'osm'
        ? { label: 'OSM', className: 'text-cyan-300 border-cyan-500/40 bg-cyan-500/10' }
        : normalizedSource === 'user'
          ? { label: t('spot.source_user'), className: 'text-pink-300 border-pink-500/40 bg-pink-500/10' }
          : { label: 'AI', className: 'text-amber-300 border-amber-500/40 bg-amber-500/10' }
    : null;

  const isAdmin = isAdminUser(user);
  const canEdit = !!user;

  const score = Math.round(spot.aiResult?.score || spot.score);
  const elevation = spot.elevation !== undefined && spot.elevation !== null ? spot.elevation : spot.aiResult?.elevation_moh;
  const hasBarrier = spot.legal?.hasBarrier;
  const inProtectedArea = spot.legal?.inProtectedArea === true;
  const protectedAreaType = spot.legal?.protectedAreaType;
  const protectedAreaName = spot.legal?.protectedAreaName;
  const protectedAreaTypeLabel = protectedAreaType === 'NATIONALPARK' ? t('spot.nationalpark')
    : protectedAreaType === 'NATURRESERVAT' ? t('spot.naturreservat')
    : protectedAreaType === 'NATURMINNE' ? t('spot.naturminne')
    : t('spot.protected_area');
  const houseDist = spot.nearestHouse;
  const isTooClose = houseDist !== undefined && houseDist < 150;
  const farmlandDistance = spot.legal?.distToFarmland != null ? spot.legal.distToFarmland : null;

  // Brukeromtaler (avledet fra spotReviews-subscription)
  const reviewCount = spotReviews.length;
  const reviewAverage = reviewCount > 0
    ? spotReviews.reduce((sum, r) => sum + (Number((r as any)?.rating) || 0), 0) / reviewCount
    : 0;

  // Eiendoms-/områdetagger
  const spotTags = Array.isArray(spot.tags) ? spot.tags : [];
  const hasStatligFriluft = spotTags.includes('Statlig Friluftsområde');
  const hasStatskog = spotTags.includes('Statskog');

  const needsReanalysis = !isRasteplass && !isOsmFacility && !isSnuplass && !isAttractionPoi && score < 6;

  const getLegalStatus = () => {
    if (isRasteplass || isSnuplass) return { label: t('spot.public'), color: 'text-blue-400 border-blue-500/20 bg-blue-500/5' };
    if (isOsmFacility || isSnuplass) return { label: t('spot.wilderness'), color: 'text-emerald-400 border-brand/20 bg-brand/5' };
    const status = spot.aiResult?.anbefaling || 'SJEKK_SKILT';
    if (status === 'LOVLIG') return { label: t('spot.legal_allowed'), color: 'text-emerald-400 border-brand/20 bg-brand/5' };
    if (status === 'FORBUDT') return { label: t('spot.forbidden'), color: 'text-red-400 border-red-500/20 bg-red-500/5' };
    return { label: t('spot.check_signs'), color: 'text-amber-400 border-amber-500/20 bg-amber-500/5' };
  };

  const legalStatus = getLegalStatus();

  // Kritiske varsler som ALDRI skal gjemmes bak en fane — vises som et
  // vedvarende bånd over fane-menyen uansett hvilken fane som er aktiv.
  const recommendation = spot.aiResult?.anbefaling;
  const isForbidden = recommendation === 'FORBUDT' && !isRasteplass && !isSnuplass && !isOsmFacility;
  const isOccupied = isSpotOccupied(spot);
  const hasWaterWarning = !!spot.bathingMetadata?.waterQualityWarning;
  const criticalWarnings = [
    isOccupied && {
      key: 'occupied',
      tone: 'red' as const,
      Icon: Tent,
      label: occupancyDaysRemaining(spot.occupiedUntil!) <= 1
        ? t('spot.occupied.banner_1')
        : t('spot.occupied.banner', { days: occupancyDaysRemaining(spot.occupiedUntil!) }),
    },
    isForbidden && {
      key: 'forbidden',
      tone: 'red' as const,
      Icon: Ban,
      label: t('spot.forbidden'),
    },
    hasWaterWarning && {
      key: 'water',
      tone: 'red' as const,
      Icon: Droplets,
      label: spot.bathingMetadata?.warningText || t('spot.water_quality_warning', 'Advarsel om vannkvalitet'),
    },
    isTooClose && {
      key: 'house',
      tone: 'amber' as const,
      Icon: Home,
      label: t('spot.house_proximity_warning', { defaultValue: 'Kun {{dist}}m fra bebyggelse — vis ekstra hensyn', dist: Math.round(houseDist!) }),
    },
  ].filter(Boolean) as Array<{ key: string; tone: 'red' | 'amber'; Icon: typeof Home; label: string }>;

  // Score-farge til kollapset «dom»-visning (speiler desktop-kortet)
  const showScore = Number.isFinite(score) && !isRasteplass && !isSnuplass && !isOsmFacility && !isAttractionPoi;
  const scoreColor = score >= 8 ? 'text-brand' : score < 4 ? 'text-red-500' : 'text-amber-500';

  const handleShare = async () => {
    const shareUrl = `${APP_URL}/share/spot/${spot.id}`;
    // Best effort: generate and persist missing title/description for better share previews.
    ensureSpotShareMetadata(spot).catch((err) => {
      console.warn('Could not ensure share metadata for spot', err);
    });

    if (navigator.share) {
      try {
        await navigator.share({
          title: spot.name,
          text: t('spot.sharing_text', { name: spot.name }),
          url: shareUrl
        });
        logFeatureUsage('share');
      } catch (err) {
        console.error("Deling avbrutt");
      }
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast(t('common.link_copied'), 'success');
    }
  };

  const handleReport = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) { toast(t('common.login_to_report'), 'warning'); return; }
    if (!confirmReport) { setConfirmReport(true); return; }
    setIsProcessing(true);
    try {
      await suggestDeletion(spot.id, user.uid, "Rapportert via mobil", user.displayName || user.email);
      if (onRemoveSpot) onRemoveSpot(spot.id);
      onClose();
    } catch (err) {
      toast(t('common.report_failed'), 'error');
    } finally {
      setIsProcessing(false);
      setConfirmReport(false);
    }
  };

  const handleAdminDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isAdmin) return;
    if (!confirmDelete) { console.log('[ADMIN-DELETE] First tap — showing confirm for', spot.id, spot.name); setConfirmDelete(true); return; }
    console.log('[ADMIN-DELETE] Confirmed! Starting delete for', spot.id, spot.name);
    setIsProcessing(true);
    try {
      // wpcard- spots er syntetiske CampingSpots avledet fra brukerens eget waypoint
      // (visit, legacy_wp_, eller user_content). De finnes ikke i cached_spots, så
      // banSpotGlobally feiler. Rute til riktig sletting basert på underliggende kilde.
      if (spot.id.startsWith('wpcard-')) {
        const wpId = spot.id.slice('wpcard-'.length);
        console.log('[ADMIN-DELETE] wpcard- spot — wpId =', wpId, '| user =', user?.uid);
        if (wpId.startsWith('visit_')) {
          if (!user?.uid) throw new Error('No user for visit delete');
          const visitedSpotId = wpId.slice('visit_'.length);
          const ref = doc(db, 'user_visits', user.uid);
          console.log('[ADMIN-DELETE] visit — visitedSpotId =', visitedSpotId, '| ref =', `user_visits/${user.uid}`);
          const snap = await getDoc(ref);
          if (!snap.exists()) {
            console.warn('[ADMIN-DELETE] user_visits doc does not exist');
            throw new Error('No visits doc found');
          }
          const visits = (snap.data()?.visits || []) as any[];
          console.log('[ADMIN-DELETE] visits before:', visits.length, '| sample:', visits[0]);
          const filtered = visits.filter(v => v.spotId !== visitedSpotId);
          console.log('[ADMIN-DELETE] visits after filter:', filtered.length);
          await setDoc(ref, { visits: filtered }, { merge: true });
          console.log('[ADMIN-DELETE] setDoc completed');
        } else if (wpId.startsWith('legacy_wp_')) {
          const realId = wpId.slice('legacy_wp_'.length);
          console.log('[ADMIN-DELETE] legacy waypoint — deleting user_waypoints/' + realId);
          await deleteDoc(doc(db, 'user_waypoints', realId));
          console.log('[ADMIN-DELETE] deleteDoc completed');
        } else {
          console.log('[ADMIN-DELETE] user_content waypoint — deleteContent(' + wpId + ')');
          await deleteContent(wpId);
          console.log('[ADMIN-DELETE] deleteContent completed');
        }
      } else {
        await banSpotGlobally(spot.id, "Admin sletting mobil");
      }
      console.log('[ADMIN-DELETE] delete succeeded. onRemoveSpot exists?', !!onRemoveSpot);
      if (onRemoveSpot) {
        console.log('[ADMIN-DELETE] Calling onRemoveSpot(removeSpotLocally) for', spot.id);
        onRemoveSpot(spot.id);
      }
      console.log('[ADMIN-DELETE] Calling onClose()');
      onClose();
    } catch (err) {
      console.error('[ADMIN-DELETE] FAILED for', spot.id, err);
      toast(t('common.delete_failed'), 'error');
    } finally {
      setIsProcessing(false);
      setConfirmDelete(false);
    }
  };

  return (
    <div className={`${forceShow ? '' : 'md:hidden fixed bottom-0 left-0 right-0 z-[3500]'}`}>
      <div className={`bg-[var(--bg-surface)] border-t border-x border-brand/30 rounded-t-modal shadow-[0_-10px_40px_rgba(0,0,0,0.8)] overflow-hidden transition-[height,min-height,max-height] duration-300 ease-out flex flex-col ${isExpanded ? 'h-[92dvh] min-h-[92dvh] max-h-[92dvh]' : 'h-[38dvh] min-h-[38dvh] max-h-[38dvh]'}`}>
        
        {/* Edge-to-Edge Image/Satellite Banner */}
        <div className="h-40 sm:h-48 w-full relative overflow-hidden flex-shrink-0">
          {allImages.length > 0 && !bannerImageFailed ? (
            <img
              src={allImages[0].url}
              alt={spot.name}
              className="w-full h-full object-cover"
              onError={() => setBannerImageFailed(true)}
            />
          ) : (() => {
            const info = getSatelliteTileInfo(spot.location.latitude, spot.location.longitude, 16);
            return (
              <div className="relative w-full h-full bg-stone-800 overflow-hidden">
                <div className="absolute top-0 bottom-0 flex w-[300%]" style={{ 
                  left: `calc(-100% - ${info.offsetX}% + 50%)`,
                  top: `calc(-${info.offsetY}% + 50%)`
                }}>
                  {info.urls.map((url, i) => (
                    <img key={i} src={url} alt="" className="h-full w-1/3 object-cover" loading="lazy" decoding="async" />
                  ))}
                </div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 flex items-center justify-center">
                  <div className="absolute inset-0 rounded-full bg-brand/30 animate-ping pointer-events-none" style={{ margin: '-8px' }} />
                  <div className="absolute inset-0 rounded-full bg-brand/20 border-2 border-emerald-400/50 pointer-events-none" style={{ margin: '-4px' }} />
                  <div className="w-8 h-8 rounded-full bg-stone-950/80 backdrop-blur-sm border-2 border-white/80 shadow-lg flex items-center justify-center text-white">
                    {getSpotCardSatelliteIcon(spot, 16)}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Drag Handle Area (Overlayed) */}
          <div className="absolute top-0 left-0 right-0 z-20 flex justify-center pt-3 pb-1 cursor-pointer active:opacity-70" onClick={toggleExpand}>
            <div className="w-12 h-1.5 bg-white/60 backdrop-blur-sm rounded-full shadow-sm" />
          </div>

          {/* Close Button (Overlayed) */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 z-20 p-2 bg-black/60 backdrop-blur-sm hover:bg-black/80 rounded-full text-white/95 active:scale-95 transition shadow-md"
            title={t("common.close", "Lukk")}
            aria-label={t("common.close", "Lukk")}
          >
            <X size={18} />
          </button>
        </div>

        {/* Card Body content */}
        <div className="flex-1 flex flex-col p-5 pt-3 gap-3 overflow-hidden min-h-0" style={{ paddingBottom: 'calc(var(--safe-area-bottom, 0px) + 1.5rem)' }}>
          {/* Header Row: Title & Basic info */}
          <div className="flex justify-between items-start flex-shrink-0">
            <div className="flex-1 pr-2 min-w-0" onClick={toggleExpand}>
              <div className="flex items-center gap-1.5">
                <h3 className="text-[var(--text-primary)] font-black text-sm tracking-tight truncate">{spot.name}</h3>
                {canEdit && !isSearchResult && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onEdit?.(spot); }}
                    className="p-1 bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] rounded-lg text-emerald-400 transition flex-shrink-0"
                  >
                    <Edit3 size={12} />
                  </button>
                )}
              </div>
              <div className="flex items-center flex-wrap gap-2 mt-1 text-3xs font-black uppercase text-[var(--text-muted)]">
                {spot.distance !== undefined && (
                  <span className="flex items-center gap-0.5">
                    <MapPin size={10} /> {(spot.distance / 1000).toFixed(1)} km
                  </span>
                )}
                {elevation !== undefined && elevation !== null && (
                  <span className="flex items-center gap-0.5">
                    <MountainSnow size={10} /> {elevation} m
                  </span>
                )}
                <span className={`px-1 rounded border flex items-center gap-0.5 ${hasBarrier ? 'text-red-500 border-red-500/20 bg-red-500/5' : 'text-brand border-brand/20 bg-brand/5'}`}>
                  {hasBarrier ? <Lock size={9} /> : <Unlock size={9} />}
                  <span className="text-[9px]">{hasBarrier ? t('spot.barrier_badge') : t('spot.open_badge')}</span>
                </span>
                {typeof spot.googleRating === 'number' && (
                  <span className="flex items-center gap-0.5 text-amber-400" title={t('spot.google_rating', 'Google-vurdering')}>
                    <Star size={10} className="fill-amber-400" />
                    {spot.googleRating.toFixed(1)}
                    {spot.googleRatingCount ? <span className="text-[var(--text-muted)]">({spot.googleRatingCount})</span> : null}
                  </span>
                )}
                {/* Klikkbar butikk/lekeplass-avstand → rute-overlegg */}
                <SpotProximityFacts spot={spot} active compact />
              </div>
            </div>
          </div>

          {/* Kritiske varsler — alltid synlig, uavhengig av fane */}
          {criticalWarnings.length > 0 && (
            <div className="flex flex-col gap-1.5 flex-shrink-0">
              {criticalWarnings.map(({ key, tone, Icon, label }) => (
                <div
                  key={key}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${tone === 'red' ? 'bg-red-500/10 border-red-500/30 text-red-400' : 'bg-amber-500/10 border-amber-500/30 text-amber-400'}`}
                >
                  <Icon size={14} className="shrink-0" />
                  <span className="text-3xs font-bold leading-tight">{label}</span>
                  {key === 'occupied' && spot.occupiedBy === user?.uid && onClearOccupancy && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onClearOccupancy(spot.id); }}
                      className="ml-auto text-4xs underline shrink-0"
                    >
                      {t('spot.occupied.check_out', 'Sjekk ut')}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Tab Menu */}
          <div className="flex w-full bg-[var(--bg-surface-alt)] p-0.5 rounded-xl border border-[var(--border-primary)] flex-shrink-0">
            <button
              onClick={() => handleTabClick('overview')}
              className={`flex-1 py-1.5 rounded-lg text-3xs font-black uppercase tracking-widest transition whitespace-nowrap text-center ${activeTab === 'overview' ? 'bg-brand-dark text-white shadow-md' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}
            >
              {t('spot.tabs.overview', 'Oversikt')}
            </button>
            <button
              onClick={() => handleTabClick('legal')}
              className={`flex-1 py-1.5 rounded-lg text-3xs font-black uppercase tracking-widest transition whitespace-nowrap text-center ${activeTab === 'legal' ? 'bg-brand-dark text-white shadow-md' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}
            >
              {t('spot.tabs.area', 'Område')}
            </button>
            <button
              onClick={() => handleTabClick('facilities')}
              className={`flex-1 py-1.5 rounded-lg text-3xs font-black uppercase tracking-widest transition whitespace-nowrap text-center ${activeTab === 'facilities' ? 'bg-brand-dark text-white shadow-md' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}
            >
              {t('spot.tabs.facilities', 'Fasiliteter')}
            </button>
          </div>

          {/* Tab Content (Scrollable) */}
          <div className="flex-1 overflow-y-auto min-h-0 flex flex-col gap-3 pr-0.5 custom-scrollbar" style={{ overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}>
            {isExpanded ? (
              <>
                {activeTab === 'overview' && (
                  <>
                    {needsReanalysis && !isAnalyzing && (
                      <div className="bg-amber-500/10 p-2.5 rounded-2xl border border-amber-500/20 flex items-center justify-between flex-shrink-0">
                        <p className="text-4xs font-bold text-amber-500 uppercase tracking-wider">{t('spot.potential_reanalyze')}</p>
                        <button onClick={(e) => { e.stopPropagation(); onReanalyze(spot); }} className="text-amber-500 p-1">
                          <RefreshCcw size={12} />
                        </button>
                      </div>
                    )}

                    {isAnalyzing && (
                      <div className="bg-brand/10 p-2.5 rounded-2xl border border-brand/20 flex items-center gap-2 flex-shrink-0">
                        <Loader2 size={12} className="animate-spin text-emerald-400" />
                        <p className="text-4xs font-bold text-emerald-400 uppercase tracking-widest">{t('spot.analyzing')}</p>
                      </div>
                    )}

                    {isGeneratingMeta && (
                      <div className="bg-brand/10 p-2.5 rounded-2xl border border-brand/20 flex items-center gap-2 flex-shrink-0">
                        <Loader2 size={12} className="animate-spin text-emerald-400" />
                        <p className="text-4xs font-bold text-emerald-400 uppercase tracking-widest">{t('spot.generating_meta')}</p>
                      </div>
                    )}

                    {spot.aiResult && !isAnalyzing && !isGeneratingMeta && !isAttractionPoi && (
                      <div className="bg-brand/5 border border-brand/10 p-3 rounded-2xl flex-shrink-0">
                        <p className="text-2xs text-[var(--text-secondary)] italic font-medium leading-relaxed">
                          "{spot.aiResult.vurdering}"
                        </p>
                      </div>
                    )}

                    {spot.description && (
                      <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] p-3 rounded-2xl flex-shrink-0">
                        <p className="text-2xs text-[var(--text-secondary)] leading-relaxed">{spot.description}</p>
                      </div>
                    )}

                    {/* Kontaktinfo for parker/POI-er (nettside, telefon, åpningstider) */}
                    {(isAttractionPoi || isCommercialCampsite) && <ContactInfoBlock spot={spot} />}

                    {/* Weather Sun Widget */}
                    <div className="flex-shrink-0">
                      <WeatherSunBadge lat={spot.location.latitude} lon={spot.location.longitude} />
                    </div>

                    {/* Nature / Wildlife sightings — «hvorfor hit», hører hjemme i Oversikt */}
                    {wildlife.species.length > 0 && (
                      <div className="flex-shrink-0">
                        <NatureScoutCard
                          lat={spot.location.latitude}
                          lon={spot.location.longitude}
                          species={wildlife.species}
                          latestSeen={wildlife.latestSeen}
                          loading={wildlife.loading}
                          error={wildlife.error}
                        />
                      </div>
                    )}

                    {/* User Review Summary */}
                    {reviewCount > 0 && (
                      <div className="bg-amber-500/5 border border-amber-500/20 p-3 rounded-2xl flex items-center gap-2.5 flex-shrink-0">
                        <Star size={14} className="text-amber-400 fill-amber-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-4xs font-black uppercase tracking-widest text-amber-400">{t('spot.reviews_title', 'Brukeromtaler')}</p>
                          <p className="text-2xs text-[var(--text-secondary)] font-bold">
                            {reviewAverage.toFixed(1)} av 5 stjerner ({reviewCount} {t('spot.reviews_count_label', 'anmeldelser')})
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Omtaler: brukeromtaler + Google flettet (dato-sortert, siste 3 + vis flere) */}
                    {!isSearchResult && (
                      <ReviewSystem
                        spotId={spot.id}
                        spotName={spot.name}
                        user={user}
                        onOpenAuth={onOpenAuth || (() => {})}
                        spotIsVerified={!!spot.isVerified || spot.spotType === 'commercial_campsite' || spot.spotType === 'rasteplass' || spot.spotType === 'osm_amenity' || spot.spotType === 'coastal'}
                        spotHasPhoto={allImages.length > 0}
                        onOpenVerification={() => onEdit?.(spot)}
                      />
                    )}

                    {/* Other photos thumbnails */}
                    {allImages.length > 1 && (
                      <div className="space-y-1 flex-shrink-0">
                        <p className="text-4xs font-black uppercase tracking-widest text-[var(--text-muted)]">{t('spot.details.photos_title', 'Flere bilder')}</p>
                        <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1">
                          {allImages.slice(1, 6).map((img, i) => (
                            <div key={i} className="relative flex-shrink-0">
                              <img
                                src={getThumbUrl(img.url)}
                                alt=""
                                className="w-24 h-16 rounded-xl object-cover border border-[var(--border-primary)]"
                                loading="lazy"
                                onError={(e) => {
                                  const el = e.target as HTMLImageElement;
                                  if (img.url && el.src !== img.url) el.src = img.url;
                                }}
                              />
                              {img.source === 'google' && (
                                <span className="absolute bottom-1 right-1 text-4xs bg-black/60 text-white px-1 rounded leading-tight">Google</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {activeTab === 'legal' && (
                  <>
                    {/* Allemannsretten & Legal Badge — ikke relevant for fornøyelsesparker o.l. */}
                    {!isAttractionPoi && (
                      <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] p-3 rounded-2xl space-y-2 flex-shrink-0">
                        <div className="flex items-center gap-2">
                          <ShieldCheck className="text-emerald-400" size={16} />
                          <span className="text-2xs font-bold text-[var(--text-primary)]">{legalStatus.label}</span>
                        </div>
                        {spot.aiResult?.juridisk_tolking && (
                          <p className="text-3xs text-[var(--text-secondary)] leading-relaxed">{spot.aiResult.juridisk_tolking}</p>
                        )}
                      </div>
                    )}

                    {/* Avstand til bebyggelse — selve advarselen vises i det kritiske båndet
                        over fanene, så her viser vi kun den betryggende «trygt unna»-varianten. */}
                    {houseDist !== undefined && !isTooClose && (
                      <div className="p-3 rounded-2xl border flex items-start gap-2.5 flex-shrink-0 bg-brand/10 border-brand/25 text-emerald-400">
                        <Home size={14} className="mt-0.5 shrink-0" />
                        <div className="text-2xs font-bold leading-normal">
                          {t('spot.far_from_house_desc', { defaultValue: 'Plassen ligger trygt plassert {{dist}}m fra nærmeste bebyggelse.', dist: Math.round(houseDist) })}
                        </div>
                      </div>
                    )}

                    {/* Property & Land status */}
                    <div className="grid grid-cols-2 gap-2 text-3xs font-black uppercase text-[var(--text-secondary)] flex-shrink-0">
                      {inProtectedArea && (
                        <div className="bg-amber-500/5 border border-amber-500/20 p-2.5 rounded-xl flex items-center gap-1.5 text-amber-400">
                          <i className="fa-solid fa-tree text-[9px]" />
                          <span>Verneområde</span>
                        </div>
                      )}
                      {hasStatskog && (
                        <div className="bg-green-500/5 border border-green-500/20 p-2.5 rounded-xl flex items-center gap-1.5 text-green-400">
                          <i className="fa-solid fa-mountain text-[9px]" />
                          <span>Statskog</span>
                        </div>
                      )}
                      {hasStatligFriluft && (
                        <div className="bg-brand/5 border border-brand/20 p-2.5 rounded-xl flex items-center gap-1.5 text-emerald-400">
                          <i className="fa-solid fa-tree text-[9px]" />
                          <span>Statlig friluft</span>
                        </div>
                      )}
                      {farmlandDistance !== null && farmlandDistance < 150 && (
                        <div className="bg-amber-500/5 border border-amber-500/20 p-2.5 rounded-xl flex items-center gap-1.5 text-amber-400 col-span-2">
                          <Lock size={10} />
                          <span>{Math.round(farmlandDistance)}m til dyrka mark</span>
                        </div>
                      )}
                    </div>

                    {/* Hiking trails */}
                    {spot.hikingTrails && spot.hikingTrails.length > 0 && (
                      <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] p-3 rounded-2xl space-y-1.5 flex-shrink-0">
                        <p className="text-4xs font-black uppercase tracking-widest text-emerald-400">{t('spot.details.hiking.title')}</p>
                        {spot.hikingTrails.slice(0, 3).map((trail, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <Mountain size={10} className="text-brand flex-shrink-0" />
                            <span className="text-2xs text-[var(--text-secondary)] truncate">{trail.name || t('spot.hiking.unnamed_trail')}</span>
                            {trail.totalLength && <span className="text-3xs text-[var(--text-muted)] flex-shrink-0">{(trail.totalLength / 1000).toFixed(1)} km</span>}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Nærliggende webkameraer — område-info */}
                    {nearbyCameras.length > 0 && (
                      <div className="flex-shrink-0">
                        <WebcamBlock cameras={nearbyCameras} failedIds={failedCameraIds} onImageError={(id) => setFailedCameraIds(prev => new Set(prev).add(id))} />
                      </div>
                    )}

                  </>
                )}

                {activeTab === 'facilities' && (
                  <>
                    {/* Category Facilities tag list */}
                    <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] p-3 rounded-2xl space-y-2 flex-shrink-0">
                      <p className="text-4xs font-black uppercase tracking-widest text-brand">{t('spot.facilities_title', 'Tjenester og Fasiliteter')}</p>
                      <div className="flex flex-wrap gap-1.5 text-3xs font-black uppercase text-[var(--text-secondary)]">
                        {isRasteplass && (
                          <span className="bg-blue-500/10 border border-blue-500/20 px-2 py-0.5 rounded-xl text-blue-400">Rasteplass</span>
                        )}
                        {isSnuplass && (
                          <span className="bg-blue-500/10 border border-blue-500/20 px-2 py-0.5 rounded-xl text-blue-400">Snuplass</span>
                        )}
                        {isGapahuk && (
                          <span className="bg-brand/10 border border-brand/20 px-2 py-0.5 rounded-xl text-brand">Gapahuk</span>
                        )}
                        {isFirepit && (
                          <span className="bg-orange-500/10 border border-orange-500/20 px-2 py-0.5 rounded-xl text-orange-400">Bålplass</span>
                        )}
                        {isPicnic && (
                          <span className="bg-blue-500/10 border border-blue-500/20 px-2 py-0.5 rounded-xl text-blue-400">Piknikbord</span>
                        )}
                      </div>
                    </div>

                    {/* Severdigheter */}
                    {!noAttractionsFound && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleToggleAttractions(); }}
                        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-2xl border transition text-3xs font-black uppercase tracking-widest flex-shrink-0 self-start ${showAttractions ? 'bg-violet-500 text-white border-violet-500' : 'text-violet-400 bg-violet-500/5 border-violet-500/30'}`}
                      >
                        {isLoadingAttractions ? <Loader2 size={12} className="animate-spin" /> : <Landmark size={12} />}
                        {t('spot.attractions')}
                      </button>
                    )}
                    {showAttractions && (
                      <div className="flex-shrink-0">
                        <AttractionsBlock spot={spot} attractions={attractions} />
                      </div>
                    )}

                    {/* Skaper — hvem la plassen til */}
                    {normalizedSource === 'user' && (
                      <div className="flex-shrink-0">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); if (spot.creatorVehicleCategory) setShowCreatorVehicle(v => !v); }}
                          className={`inline-flex items-center gap-1.5 text-3xs font-black pl-0.5 pr-2 py-0.5 rounded-full border text-pink-300 border-pink-500/40 bg-pink-500/10 ${spot.creatorVehicleCategory ? 'cursor-pointer' : 'cursor-default'}`}
                          title={spot.createdByName ? t('spot.added_by', { name: spot.createdByName, defaultValue: 'Lagt til av {{name}}' }) : t('spot.source_user')}
                        >
                          <UserAvatar photoURL={spot.createdByPhoto || undefined} size="xs" />
                          {spot.createdByName
                            ? t('spot.added_by', { name: spot.createdByName, defaultValue: 'Lagt til av {{name}}' })
                            : t('spot.source_user')}
                        </button>
                        {showCreatorVehicle && spot.creatorVehicleCategory && (
                          <div className="mt-1.5">
                            <VehicleBadge category={spot.creatorVehicleCategory} length={spot.creatorVehicleLength} is4wd={spot.creatorVehicleIs4wd} compact />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Road Connection */}
                    {(spot as any).roadConnection && (
                      <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] p-3 rounded-2xl space-y-1 flex-shrink-0">
                        <p className="text-4xs font-black uppercase tracking-widest text-blue-400">{t('spot.details.road_connection.title')}</p>
                        <p className="text-2xs text-[var(--text-secondary)]">
                          {(spot as any).roadConnection.roadType}{(spot as any).roadConnection.distanceM ? ` — ${Math.round((spot as any).roadConnection.distanceM)} m` : ''}
                        </p>
                      </div>
                    )}

                    {/* Charger info */}
                    {spot.nearestCharger && (
                      <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] p-3 rounded-2xl flex items-center gap-2.5 flex-shrink-0">
                        <Zap size={14} className="text-amber-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-4xs font-black uppercase tracking-widest text-amber-400">{t('spot.details.charger.nearest_charger')}</p>
                          <p className="text-2xs text-[var(--text-secondary)] truncate">
                            {spot.nearestCharger.name || t('spot.details.charger.station')}{spot.nearestCharger.distance ? ` — ${(spot.nearestCharger.distance / 1000).toFixed(1)} km` : ''}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Vehicle visits */}
                    {spot.vehicleVisits && Object.entries(spot.vehicleVisits).some(([, c]) => c > 0) && (
                      <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] p-3 rounded-2xl space-y-1.5 flex-shrink-0">
                        <p className="text-4xs font-black uppercase tracking-widest text-orange-400">{t('spot.vehicle_visits_title', 'Registrerte kjøretøybesøk')}</p>
                        <div className="flex flex-wrap items-center gap-1.5">
                          {Object.entries(spot.vehicleVisits).filter(([, c]) => c > 0).map(([cat, count]) => (
                            <span key={cat} className="inline-flex items-center gap-0.5">
                              <VehicleBadge category={cat as any} compact />
                              <span className="text-4xs font-black text-orange-400/60">×{count}</span>
                            </span>
                          ))}
                          {(spot.vehicleMaxLength ?? 0) > 0 && (
                            <span className="text-4xs font-black text-[var(--text-muted)] uppercase tracking-wide ml-1">
                              {t('spot.longest')}: {Math.round(spot.vehicleMaxLength! / 100)}m
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Activity History */}
                    {(checkInCount > 0 || checkInNights > 0 || spot.lastCleanedUp) && (
                      <button
                        type="button"
                        onClick={() => {
                          if (checkInCount > 0 || checkInNights > 0) setShowVehicleVisitsOverlay(true);
                        }}
                        disabled={!(checkInCount > 0 || checkInNights > 0)}
                        className={`bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] p-3 rounded-2xl space-y-1.5 text-4xs font-black uppercase tracking-wide text-[var(--text-muted)] flex-shrink-0 w-full text-left ${(checkInCount > 0 || checkInNights > 0) ? 'active:scale-[0.99] transition-transform' : ''}`}
                      >
                        <p className="text-4xs font-black uppercase tracking-widest text-teal-400">{t('spot.history_title', 'Aktivitetshistorikk')}</p>
                        <div className="flex flex-col gap-1">
                          {checkInCount > 0 && (
                            <span>{t('spot.checkins_count', { defaultValue: 'Innsjekker' })}: {checkInCount}</span>
                          )}
                          {checkInNights > 0 && (
                            <span>{t('spot.checkins_nights', { defaultValue: 'Netter' })}: {checkInNights}</span>
                          )}
                          {(checkInCount > 0 || checkInNights > 0) && (
                            <span className="text-teal-400/80 normal-case font-bold text-3xs">
                              {t('spot.vehicle_visits.open_hint', 'Se kjøretøy som har vært her')} →
                            </span>
                          )}
                          {spot.lastCleanedUp && (
                            <span className="inline-flex items-center gap-1 text-teal-400">
                              <Recycle size={9} />
                              Ryddet {formatCleanupAge(spot.lastCleanedUp)}
                            </span>
                          )}
                        </div>
                      </button>
                    )}

                    {/* Secondary actions button group */}
                    {!isSearchResult && user && (
                      <div className="space-y-1.5 flex-shrink-0">
                        <p className="text-4xs font-black uppercase tracking-widest text-[var(--text-muted)]">{t('spot.more_actions', 'Flere handlinger')}</p>
                        <div className="flex gap-2 flex-wrap">
                          <button
                            onClick={handleReport}
                            disabled={isProcessing}
                            className={`min-w-[44px] min-h-[44px] px-3 py-2.5 rounded-2xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 active:scale-95 border transition-colors ${confirmReport ? 'bg-amber-600 text-white border-amber-500 animate-pulse' : 'bg-[var(--bg-input)] text-[var(--text-secondary)] border-[var(--border-primary)] hover:text-[var(--text-primary)]'}`}
                          >
                            {isProcessing ? <Loader2 size={16} className="animate-spin" /> : <Ban size={16} />}
                          </button>

                          {isAdmin && (
                            <button
                              onClick={handleAdminDelete}
                              disabled={isProcessing}
                              className={`min-w-[44px] min-h-[44px] px-3 py-2.5 rounded-2xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 active:scale-95 border transition-colors ${confirmDelete ? 'bg-red-600 text-white border-red-500 animate-pulse' : 'bg-red-900/20 text-red-500 border-red-500/20 hover:bg-red-900/40'}`}
                            >
                              {isProcessing ? <Loader2 size={16} className="animate-spin" /> : <ShieldAlert size={16} />}
                            </button>
                          )}

                          {onMarkOccupied && isNearby && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setShowVisitModal(true); }}
                              className={`min-w-[44px] min-h-[44px] px-3 py-2.5 rounded-2xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 active:scale-95 border transition ${isVisited ? 'bg-brand/20 text-emerald-400 border-brand/30' : 'bg-[var(--bg-input)] text-[var(--text-muted)] border-[var(--border-primary)] hover:text-[var(--text-primary)]'}`}
                              title={isVisited ? 'Fjern besøksmarkering' : 'Marker som besøkt'}
                            >
                              <CheckCircle2 size={16} fill={isVisited ? 'currentColor' : 'none'} />
                            </button>
                          )}
                          {/* Etterregistrer besøk når man ikke er i nærheten (innsjekk krever <150m) */}
                          {onAddPastStay && !isVisited && !isNearby && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setShowPastVisitModal(true); }}
                              className="min-w-[44px] min-h-[44px] px-3 py-2.5 rounded-2xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 active:scale-95 border bg-[var(--bg-input)] text-[var(--text-muted)] border-[var(--border-primary)] hover:text-emerald-400 transition"
                              title={t('spot.past_visit_button', 'Har vært her tidligere')}
                            >
                              <Calendar size={16} />
                            </button>
                          )}
                          {onMarkOccupied && isNearby && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setShowVisitModal(true); }}
                              className={`min-w-[44px] min-h-[44px] px-3 py-2.5 rounded-2xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 active:scale-95 border transition ${conditions.length > 0 ? 'bg-orange-500/20 text-orange-400 border-orange-500/30' : 'bg-[var(--bg-input)] text-[var(--text-muted)] border-[var(--border-primary)] hover:text-[var(--text-primary)]'}`}
                              title={t('spot.conditions.title')}
                            >
                              <Flag size={16} fill={conditions.length > 0 ? 'currentColor' : 'none'} />
                            </button>
                          )}
                          {isNearby && user && (
                            <button
                              onClick={handleCleanup}
                              disabled={cleanupLoading}
                              className="min-w-[44px] min-h-[44px] px-3 py-2.5 rounded-2xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 active:scale-95 border bg-teal-500/10 text-teal-400 border-teal-500/30 hover:bg-teal-500/20 transition"
                              title="Loggfør opprydning"
                            >
                              {cleanupLoading ? <Loader2 size={16} className="animate-spin" /> : <Recycle size={16} />}
                            </button>
                          )}
                          {onAddToMap && (
                            <button
                              onClick={(e) => { e.stopPropagation(); onAddToMap(e, spot.id); }}
                              className="min-w-[44px] min-h-[44px] px-3 py-2.5 rounded-2xl bg-[var(--bg-input)] text-[var(--text-muted)] border border-[var(--border-primary)] hover:text-sky-400 text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 active:scale-95 transition"
                              title={t('myContent.save_title')}
                            >
                              <FolderPlus size={16} />
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              /* Kollapset «dom» — gir svar på «er dette en bra plass?» uten å utvide */
              <div className="flex-grow flex flex-col justify-center gap-2 cursor-pointer select-none" onClick={toggleExpand}>
                <div className="flex items-center gap-2 flex-wrap">
                  {showScore && (
                    <span className={`text-2xl font-black italic leading-none ${scoreColor}`}>{score}</span>
                  )}
                  {isAttractionPoi ? (
                    <span className="px-2 py-1 rounded-lg border text-3xs font-black uppercase tracking-widest flex items-center gap-1 text-pink-300 border-pink-500/40 bg-pink-500/10">
                      <FerrisWheel size={11} /> {t(`types.${spot.attractionCategory || 'attraction'}`, t('types.attraction', 'Attraksjon'))}
                    </span>
                  ) : (
                    <span className={`px-2 py-1 rounded-lg border text-3xs font-black uppercase tracking-widest flex items-center gap-1 ${legalStatus.color}`}>
                      <ShieldCheck size={11} /> {legalStatus.label}
                    </span>
                  )}
                </div>
                {spot.aiResult?.vurdering ? (
                  <p className="text-2xs text-[var(--text-secondary)] italic leading-relaxed line-clamp-2">
                    "{spot.aiResult.vurdering}"
                  </p>
                ) : spot.description ? (
                  <p className="text-2xs text-[var(--text-secondary)] leading-relaxed line-clamp-2">{spot.description}</p>
                ) : null}
                <span className="inline-flex items-center gap-1 text-4xs font-black uppercase tracking-widest text-[var(--text-muted)] mt-0.5">
                  <ChevronUp size={11} /> {t('spot.tap_to_see_details', 'Trykk for å se detaljer')}
                </span>
              </div>
            )}
          </div>

          {/* Forleng oppholdet — egen rad når brukeren står her selv (sjekket inn, ikke ut) */}
          {isStayingHere && onExtendOccupancy && (
            <div className="flex flex-shrink-0 mt-auto">
              <ExtendStayControl variant="sheet" onExtend={(days) => onExtendOccupancy(spot.id, days)} />
            </div>
          )}

          {/* Primary actions row (Always visible at the bottom) */}
          <div className={`flex gap-2 flex-shrink-0 pt-2 border-t border-[var(--border-primary)] ${isStayingHere && onExtendOccupancy ? 'mt-2' : 'mt-auto'}`}>
            {isStayingHere && onClearOccupancy ? (
              <button
                onClick={(e) => { e.stopPropagation(); onClearOccupancy(spot.id); }}
                className="flex-1 bg-[var(--bg-input)] text-[var(--text-primary)] border border-[var(--border-primary)] py-3 rounded-2xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 active:scale-95"
              >
                <LogOut size={16} strokeWidth={3} /> {t('spot.occupied.check_out', 'Sjekk ut')}
              </button>
            ) : onMarkOccupied && isNearby && user ? (
              <button
                onClick={(e) => { e.stopPropagation(); setShowVisitModal(true); }}
                className="flex-1 bg-amber-500 text-stone-950 py-3 rounded-2xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 active:scale-95 shadow-[0_4px_15px_rgba(245,158,11,0.35)]"
              >
                <Tent size={16} strokeWidth={3} /> {t('spot.occupied.check_in')}
              </button>
            ) : (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const { latitude: lat, longitude: lon } = spot.location;
                  const isTesla = /Tesla/.test(navigator.userAgent);

                  if (isTesla) {
                    setShowTeslaQr(true);
                  } else {
                    openExternalNavigation(lat, lon);
                  }
                }}
                className="flex-1 bg-brand-dark text-white py-3 rounded-2xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 active:scale-95 shadow-lg"
              >
                <Navigation size={16} /> {t('common.navigate')}
              </button>
            )}
            <button
              onClick={(e) => onToggleFavorite(e, spot.id)}
              aria-pressed={isFavorite}
              title={isFavorite ? t('spot.remove_favorite', 'Fjern favoritt') : t('spot.add_favorite', 'Legg til favoritt')}
              aria-label={isFavorite ? t('spot.remove_favorite', 'Fjern favoritt') : t('spot.add_favorite', 'Legg til favoritt')}
              className={`px-4 py-3 rounded-2xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 active:scale-95 border transition-colors ${isFavorite ? 'bg-red-500/15 text-red-500 border-red-500/30' : 'bg-[var(--bg-input)] text-[var(--text-muted)] border-[var(--border-primary)] hover:text-red-400'}`}
            >
              <Heart size={16} fill={isFavorite ? 'currentColor' : 'none'} />
            </button>
            <button
              onClick={handleShare}
              className="px-5 py-3 rounded-2xl bg-brand text-stone-950 text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 active:scale-95 shadow-[0_4px_15px_rgba(16,185,129,0.3)]"
            >
              <Share2 size={16} strokeWidth={3} />
            </button>
            <button
              onClick={toggleExpand}
              className="bg-[var(--bg-input)] text-[var(--text-primary)] px-4 py-3 rounded-2xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 active:scale-95 border border-[var(--border-primary)]"
              title={isExpanded ? t("auto.mobilespotsheet.lukk_detaljer", "Lukk detaljer") : t("auto.mobilespotsheet.se_detaljer", "Se detaljer")}
              aria-label={isExpanded ? t("auto.mobilespotsheet.lukk_detaljer", "Lukk detaljer") : t("auto.mobilespotsheet.se_detaljer", "Se detaljer")}
            >
              {isExpanded ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
            </button>
          </div>
        </div>
      </div>

      {/* Tesla QR Modal */}
      {showTeslaQr && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 animate-in fade-in duration-300">
          <div className="absolute inset-0 bg-black/90 backdrop-blur-md" onClick={() => setShowTeslaQr(false)} />
          <div className="relative w-full max-w-sm bg-[var(--bg-surface)] border border-[var(--border-secondary)] rounded-modal p-8 flex flex-col items-center gap-6 shadow-3xl text-center">
            <button
              onClick={() => setShowTeslaQr(false)}
              className="absolute top-6 right-6 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
            >
              <X size={24} />
            </button>

            <div className="w-16 h-16 bg-brand/10 rounded-2xl flex items-center justify-center text-emerald-400 mb-2">
              <Navigation size={32} />
            </div>

            <div className="space-y-2">
              <h3 className="text-xl font-black text-[var(--text-primary)] tracking-tight uppercase">
                {t('tesla.nav_title')}
              </h3>
              <p className="text-xs text-[var(--text-secondary)] font-bold uppercase tracking-widest text-brand">
                {t('tesla.scan_qr')}
              </p>
            </div>

            <div className="bg-white p-4 rounded-3xl shadow-2xl">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(`${APP_URL}/share/spot/${spot.id}`)}`}
                alt="QR Code"
                className="w-48 h-48"
              />
            </div>

            <p className="text-tiny text-[var(--text-secondary)] leading-relaxed max-w-[280px]">
              {t('tesla.instruction')}
            </p>

            <button
              onClick={() => setShowTeslaQr(false)}
              className="w-full py-4 bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] text-[var(--text-primary)] rounded-2xl font-black text-xs uppercase tracking-widest transition mt-2"
            >
              {t('tesla.close')}
            </button>
          </div>
        </div>
      )}
      {/* SpotVisitModal */}
      {showVisitModal && user && onMarkOccupied && (
        <SpotVisitModal
          spot={spot}
          user={user}
          conditions={conditions}
          onClose={() => setShowVisitModal(false)}
          onConfirm={({ days, tags }) => {
            onMarkOccupied(spot.id, days, tags);
            setShowVisitModal(false);
          }}
        />
      )}

      {showVehicleVisitsOverlay && spot && (
        <VehicleVisitsOverlay
          spot={spot}
          user={user}
          onClose={() => setShowVehicleVisitsOverlay(false)}
        />
      )}

      {/* PastVisitModal — opphold-editor (Fase 2: flere atskilte opphold m/ fra–til) */}
      {showPastVisitModal && spot && user && onAddPastStay && onDeletePastStay && (
        <PastVisitModal
          spot={spot}
          userId={user.uid}
          onClose={() => setShowPastVisitModal(false)}
          onAddStay={(checkInAt, checkedOutAt) => onAddPastStay(spot.id, checkInAt, checkedOutAt)}
          onDeleteStay={(checkInAt) => onDeletePastStay(spot.id, checkInAt)}
        />
      )}
    </div>
  );
};

export default React.memo(MobileSpotSheet);
